/**
 * Which fixes are worth keeping on the way to a platform.
 *
 * Two legs are worth recording, and both end at a platform the phone cannot see him reach. In the
 * morning that is the wait at Civic Center; in the afternoon it is the whole stretch out of
 * College Prep, down to Rockridge, and onto the platform — a leg with no phase of its own, which
 * is exactly why it has to be measured rather than described.
 *
 * This is instrumentation and nothing else: nothing here moves a phase.
 *
 * Two kinds of row, because the interesting evidence is of two kinds. A *new* fix says where he
 * is and how well the phone can tell. A *repeat* — the same GPS timestamp handed out again — says
 * the phone has stopped producing fixes at all, which is itself a signal and is invisible if
 * duplicates are simply dropped.
 */

import type { RiderPhase } from "../shared/types";

/** Fixes arrive in bursts, and a burst says nothing a single fix does not. */
export const STATION_FIX_MIN_GAP_MS = 3_000;

/** A frozen fix is worth noting, not worth repeating every time the watch fires. */
export const STATION_REPEAT_MIN_GAP_MS = 15_000;

/** Enough for the walk out of school and the wait that follows it, and no more. */
export const STATION_FIX_MAX_ROWS = 400;

/**
 * Whether this phase is part of a journey to a platform.
 *
 * `on_platform` is included deliberately: at Civic Center the fix does not degrade until he is
 * already below ground, so stopping at the declaration would throw away the descent itself.
 * `at_home` counts only on the ride home, where it means standing at school rather than at a
 * house — on the school run it would record a bedroom all morning.
 */
export function shouldRecordPhase(phase: RiderPhase, hasAccessLeg: boolean): boolean {
	if (phase === "at_station" || phase === "on_platform") {
		return true;
	}
	return !hasAccessLeg && phase === "at_home";
}

export interface StationFixSample {
	/** The GPS clock. Unchanged from the last sample means the phone reissued an old fix. */
	fixAtMs: number;
}

export interface StationFixCursor {
	lastFixAtMs: number | null;
	lastReportedAtMs: number | null;
	rows: number;
}

export const EMPTY_STATION_CURSOR: StationFixCursor = { lastFixAtMs: null, lastReportedAtMs: null, rows: 0 };

export function isRepeatFix(cursor: StationFixCursor, sample: StationFixSample): boolean {
	return cursor.lastFixAtMs === sample.fixAtMs;
}

export function shouldRecordStationFix(cursor: StationFixCursor, sample: StationFixSample, nowMs: number): boolean {
	if (cursor.rows >= STATION_FIX_MAX_ROWS) {
		return false;
	}
	if (cursor.lastReportedAtMs == null) {
		return true;
	}
	const gap = isRepeatFix(cursor, sample) ? STATION_REPEAT_MIN_GAP_MS : STATION_FIX_MIN_GAP_MS;
	return nowMs - cursor.lastReportedAtMs >= gap;
}

export function advanceStationCursor(cursor: StationFixCursor, sample: StationFixSample, nowMs: number): StationFixCursor {
	return { lastFixAtMs: sample.fixAtMs, lastReportedAtMs: nowMs, rows: cursor.rows + 1 };
}
